import { useMemo, useState } from "react";
import { Check, Search } from "lucide-react";
import {
  filterMardColors,
  findMardColor,
  MARD_CATALOG_METADATA,
  MARD_SERIES,
  nearestMardColors,
  type MardCatalogEntry,
  type MardSeries,
} from "../catalog/mardCatalog";
import type { PaletteEntry } from "../core/types";
import { Sheet } from "./ui";

interface MardColorPickerProps {
  source: PaletteEntry;
  usedCatalogCodes: string[];
  onClose: () => void;
  onConfirm: (entry: MardCatalogEntry) => void;
  onCustom: () => void;
}

function ColorOption({ entry, selected, used, onClick }: {
  entry: MardCatalogEntry;
  selected: boolean;
  used: boolean;
  onClick: () => void;
}) {
  return <button
    type="button"
    className={`mard-color-option${selected ? " selected" : ""}${used ? " used" : ""}`}
    aria-label={`${entry.displayCode}，色卡 ${entry.catalogCode}，十六进制 ${entry.hex.slice(1)}${used ? "，项目中已使用" : ""}`}
    aria-pressed={selected}
    onClick={onClick}
  >
    <i style={{ backgroundColor: entry.hex }} />
    <span><b>{entry.displayCode}</b><small>{entry.displayCode === entry.catalogCode ? entry.hex : `色卡 ${entry.catalogCode} · ${entry.hex}`}</small></span>
    {used && !selected && <em>已用</em>}
    {selected && <Check />}
  </button>;
}

export function MardColorPicker({ source, usedCatalogCodes, onClose, onConfirm, onCustom }: MardColorPickerProps) {
  const initial = findMardColor(source.catalogCode ?? source.code);
  const [query, setQuery] = useState("");
  const [series, setSeries] = useState<MardSeries | "all">("all");
  const [selected, setSelected] = useState<MardCatalogEntry | null>(initial ?? null);
  const nearest = useMemo(() => nearestMardColors(source.detectedRGB ?? source.displayRGB, 8), [source]);
  const results = useMemo(() => filterMardColors(query, series), [query, series]);
  const used = useMemo(() => new Set(usedCatalogCodes.map((code) => findMardColor(code)?.catalogCode ?? code)), [usedCatalogCodes]);
  const showNearest = !query.trim() && series === "all";

  return <Sheet
    title="选择 MARD 色号"
    className="mard-picker-sheet"
    onClose={onClose}
    footer={<button className="primary full mard-confirm" disabled={!selected} onClick={() => selected && onConfirm(selected)}>
      {selected ? `确认选择 ${selected.displayCode}` : "请选择一个色号"}
    </button>}
  >
    <div className="mard-source-color">
      <i style={{ backgroundColor: `rgb(${(source.detectedRGB ?? source.displayRGB).join(",")})` }} />
      <span><b>当前待确认颜色</b><small>{source.code} · {source.detectedCount} 颗</small></span>
    </div>
    <label className="mard-search">
      <Search />
      <span className="sr-only">搜索色号或 HEX</span>
      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索色号，例如 H07 / ZG1" autoCapitalize="characters" autoCorrect="off" />
      {query && <button type="button" onClick={() => setQuery("")} aria-label="清空搜索">清除</button>}
    </label>
    <div className="mard-series" role="group" aria-label="按系列筛选">
      <button type="button" className={series === "all" ? "active" : ""} aria-pressed={series === "all"} onClick={() => setSeries("all")}>全部</button>
      {MARD_SERIES.map((item) => <button type="button" key={item} className={series === item ? "active" : ""} aria-pressed={series === item} onClick={() => setSeries(item)}>{item}</button>)}
    </div>
    <p className="mard-catalog-note">{results.length} 个色号 · HEX 仅供屏幕参考，实体颜色可能受光线和批次影响</p>
    {showNearest && <section className="mard-color-section" aria-labelledby="nearest-colors"><h3 id="nearest-colors">相近色号</h3><div className="mard-color-grid">{nearest.map((entry) => <ColorOption key={`near-${entry.catalogCode}`} entry={entry} selected={selected?.catalogCode === entry.catalogCode} used={used.has(entry.catalogCode)} onClick={() => setSelected(entry)} />)}</div></section>}
    <section className="mard-color-section" aria-labelledby="all-colors"><h3 id="all-colors">{query || series !== "all" ? "筛选结果" : "全部色号"}</h3>{results.length ? <div className="mard-color-grid">{results.map((entry) => <ColorOption key={entry.catalogCode} entry={entry} selected={selected?.catalogCode === entry.catalogCode} used={used.has(entry.catalogCode)} onClick={() => setSelected(entry)} />)}</div> : <p className="mard-empty">没有找到匹配的 MARD 色号</p>}</section>
    <button type="button" className="mard-custom-link" onClick={onCustom}>图纸不是 MARD？使用自定义色号</button>
    <p className="mard-attribution">本地色表版本 {MARD_CATALOG_METADATA.catalogVersion.replace("pixelbeads-", "")} · <a href={MARD_CATALOG_METADATA.sourceUrl} target="_blank" rel="noreferrer">来源 PixelBeads</a></p>
  </Sheet>;
}
